// frontend/src/components/layout/RoleBasedLayout.tsx
// ДОБАВЛЕНО: Глобальный индикатор training mode в шапке (Variant B)
// FIX BUG-TM-001: Добавлен loading state и fallback на localStorage для предотвращения пустого sidebar

import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress, // FIX BUG-TM-001: Добавлен для индикатора загрузки
  Alert,
  Button,
} from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTrainingMode } from '../contexts/TrainingModeContext';
import { apiUrl } from '../config/paths';

// Иконки
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // HRA Piracy
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Training badge icons
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';

import BuildVersion from '../components/BuildVersion';

// Диалог выбора оператора
import OperatorSelectionDialog from '../components/OperatorSelectionDialog';
import { Operator, OPERATORS_VERSION } from '../constants/operators.const';

const drawerWidth = 240;

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
}

// FIX BUG-TM-001: Тип для роли
type UserRole = 'admin' | 'operator' | 'client';

type DutyOperatorInfo = Partial<Operator> & {
  shortName?: string;
  fullName?: string;
  setAt?: string;
  _v?: number;
};

const isDutyRole = (role: UserRole | null) => role === 'operator' || role === 'admin';

const normalizeDutyOperator = (
  operator: DutyOperatorInfo | null | undefined
): DutyOperatorInfo | null => {
  const fullName = operator?.fullName?.trim();
  if (!fullName) return null;

  return {
    ...operator,
    fullName,
    shortName: operator?.shortName?.trim() || fullName,
    _v: OPERATORS_VERSION,
  };
};

const readStoredDutyOperator = (): DutyOperatorInfo | null => {
  const saved = localStorage.getItem('operator_info');
  if (!saved) return null;

  try {
    const parsed = JSON.parse(saved) as DutyOperatorInfo;
    if (parsed._v !== OPERATORS_VERSION) {
      localStorage.removeItem('operator_info');
      return null;
    }
    return normalizeDutyOperator(parsed);
  } catch {
    localStorage.removeItem('operator_info');
    return null;
  }
};

const persistStoredDutyOperator = (operator: DutyOperatorInfo) => {
  localStorage.setItem('operator_info', JSON.stringify({ ...operator, _v: OPERATORS_VERSION }));
};

const isDutyOperatorMissing = (operator: DutyOperatorInfo | null) => {
  const fullName = operator?.fullName?.trim();
  if (!fullName) return true;
  return /^(оператор|оператор ссто|оперативный дежурный)$/i.test(fullName);
};

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const RoleBasedLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const [operatorVersion, setOperatorVersion] = useState(0);
  const [currentDutyOperator, setCurrentDutyOperator] = useState<DutyOperatorInfo | null>(() =>
    readStoredDutyOperator()
  );
  const [currentDutyChecked, setCurrentDutyChecked] = useState(false);

  // FIX BUG-TM-001: Добавлен loading из useAuth
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Training mode
  const { isTrainingMode, stats, disableTrainingMode, loadStats } = useTrainingMode();

  // FIX BUG-TM-001: Fallback на localStorage если user ещё не загружен из контекста
  // Это решает race condition когда ProtectedRoute пропустил по localStorage,
  // но AuthContext ещё не восстановил user из токена
  const effectiveRole: UserRole | null =
    user?.role || (localStorage.getItem('role') as UserRole | null);

  const shouldShowDutyPrompt =
    isDutyRole(effectiveRole) && currentDutyChecked && isDutyOperatorMissing(currentDutyOperator);

  // Подгружаем stats при входе в layout (чтобы бейдж не висел пустым)
  useEffect(() => {
    if (isTrainingMode) {
      loadStats().catch(() => undefined);
    }
  }, [isTrainingMode, loadStats]);

  // Слушатель события смены оператора для обновления заголовка без reload
  useEffect(() => {
    const handleOperatorChange = (event: Event) => {
      const selectedOperator = normalizeDutyOperator(
        (event as CustomEvent<Operator>).detail || readStoredDutyOperator()
      );
      if (selectedOperator) {
        setCurrentDutyOperator(selectedOperator);
      } else {
        setCurrentDutyOperator(null);
      }
      setOperatorVersion((prev) => prev + 1);
    };

    const handleOpenDutyOperatorDialog = () => {
      setOperatorDialogOpen(true);
    };

    window.addEventListener('operatorChanged', handleOperatorChange);
    window.addEventListener('openDutyOperatorDialog', handleOpenDutyOperatorDialog);

    return () => {
      window.removeEventListener('operatorChanged', handleOperatorChange);
      window.removeEventListener('openDutyOperatorDialog', handleOpenDutyOperatorDialog);
    };
  }, []);

  useEffect(() => {
    if (loading || !isDutyRole(effectiveRole)) return;

    let cancelled = false;
    setCurrentDutyChecked(false);

    fetch(apiUrl('/admin/settings/duty-operator'), {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load current duty operator');
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;

        const dutyOperator = normalizeDutyOperator(data?.operator);
        if (dutyOperator) {
          setCurrentDutyOperator(dutyOperator);
          persistStoredDutyOperator(dutyOperator);
        } else {
          setCurrentDutyOperator(null);
          localStorage.removeItem('operator_info');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentDutyOperator(readStoredDutyOperator());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCurrentDutyChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveRole, loading]);

  const handleDutyOperatorDialogClose = (operator: Operator | null) => {
    if (operator) {
      const dutyOperator = normalizeDutyOperator(operator);
      if (dutyOperator) {
        setCurrentDutyOperator(dutyOperator);
        persistStoredDutyOperator(dutyOperator);
        setCurrentDutyChecked(true);
        setOperatorVersion((prev) => prev + 1);

        fetch(apiUrl('/admin/settings/duty-operator'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            shortName: dutyOperator.shortName,
            fullName: dutyOperator.fullName,
          }),
        }).catch(() => undefined);
      }
    }
    setOperatorDialogOpen(false);
  };

  // Пункты меню для оператора
  const operatorMenuItems: MenuItem[] = [
    { title: 'Заявки', path: '/operator/requests', icon: <AssignmentIcon /> },
    { title: 'Сигналы', path: '/operator/signals', icon: <NotificationsIcon /> },
    { title: 'Отчеты', path: '/operator/reports', icon: <DescriptionIcon /> },
    {
      title: 'Отчёты судовладельцев',
      path: '/operator/shipowner-reports',
      icon: <DescriptionIcon />,
    },
    { title: 'Карта', path: '/operator/map', icon: <MapIcon /> },
  ];

  // Пункты меню для клиента
  const clientMenuItems: MenuItem[] = [
    { title: 'Панель', path: '/client', icon: <DashboardIcon /> },
    { title: 'Мои заявки', path: '/client/requests', icon: <AssignmentIcon /> },
    { title: 'Отчеты', path: '/client/reports', icon: <DescriptionIcon /> },
    { title: 'Руководство', path: '/client/guide', icon: <HelpOutlineIcon /> },
  ];

  // Пункты меню для админа
  const adminMenuItems: MenuItem[] = [
    { title: 'Панель', path: '/admin', icon: <DashboardIcon /> },
    { title: 'Пользователи', path: '/admin/users', icon: <PersonIcon /> },
    { title: 'Настройки', path: '/admin/settings', icon: <SettingsIcon /> },
    { title: 'HRA Piracy', path: '/admin/piracy', icon: <WarningAmberIcon /> },
    ...operatorMenuItems
      .filter((item) => item.title !== 'Панель')
      .map((item) => ({
        ...item,
        path: item.path.replace('/operator', '/admin'),
      })),
  ];

  // FIX BUG-TM-001: Выбор меню в зависимости от effectiveRole (не user?.role)
  const getMenuItems = (): MenuItem[] => {
    switch (effectiveRole) {
      case 'admin':
        return adminMenuItems;
      case 'operator':
        return operatorMenuItems;
      case 'client':
        return clientMenuItems;
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  // FIX BUG-TM-001: Показываем индикатор загрузки пока AuthContext инициализируется
  // Это предотвращает мигание пустого sidebar
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Загрузка...
        </Typography>
      </Box>
    );
  }

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          ССТО
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleMenuClick(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <BuildVersion />
    </Box>
  );

  // FIX BUG-TM-001: Используем effectiveRole для заголовка
  // operatorVersion используется для форсирования ре-рендера при смене оператора
  const getRoleTitle = (): React.ReactNode => {
    void operatorVersion; // используется для триггера ре-рендера
    switch (effectiveRole) {
      case 'admin':
        return 'Администратор';
      case 'operator': {
        const dutyOperator = currentDutyOperator || readStoredDutyOperator();
        const fallbackName = user?.full_name?.trim();
        const displayName = !isDutyOperatorMissing(dutyOperator)
          ? dutyOperator?.fullName
          : fallbackName;
        if (displayName) {
          return (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {`ОПЕРАТОР: ОПЕРАТИВНЫЙ ДЕЖУРНЫЙ ${displayName.toUpperCase()}`}
              <IconButton
                size="small"
                onClick={() => {
                  setOperatorDialogOpen(true);
                }}
                sx={{ color: 'white', padding: '4px' }}
                title="Открыть текущее дежурство"
              >
                <AccountCircleIcon fontSize="small" />
              </IconButton>
            </span>
          );
        }
        return 'Оператор ССТО';
      }
      case 'client':
        return 'Заявитель';
      default:
        return 'Пользователь';
    }
  };

  const trainingLabel = stats
    ? `🎓 РЕЖИМ ТРЕНИРОВКИ (${stats.totalRequests} заявок, ${stats.totalSignals} сигналов)`
    : '🎓 РЕЖИМ ТРЕНИРОВКИ';

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* ===== ЛЕВАЯ ЗОНА: hamburger + заголовок ===== */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexShrink: 1, minWidth: 0 }}>
            {getRoleTitle()}
          </Typography>

          <Box sx={{ flex: 1, minWidth: 0 }} />

          {/* ===== ПРАВАЯ ЗОНА: training chip + email + avatar ===== */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {isTrainingMode && (
              <Tooltip title="Нажмите × чтобы выключить режим тренировки">
                <Chip
                  icon={<SchoolIcon />}
                  label={trainingLabel}
                  color="warning"
                  onDelete={disableTrainingMode}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    mr: 1,
                    fontWeight: 'bold',
                    '& .MuiChip-deleteIcon': { color: 'rgba(0,0,0,0.6)' },
                  }}
                />
              </Tooltip>
            )}

            <Typography variant="body2" noWrap sx={{ mr: 1, display: { xs: 'none', md: 'block' } }}>
              {user?.email}
            </Typography>

            <IconButton size="large" onClick={handleProfileMenuOpen} color="inherit">
              <AccountCircleIcon />
            </IconButton>
          </Box>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProfileMenuClose}>
            <MenuItem disabled>
              <Typography variant="body2">{user?.full_name || user?.email}</Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="textSecondary">
                {user?.organization_name}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Выход
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Основной контент + оранжевая рамка (опционально) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          border: isTrainingMode ? '3px solid #ff9800' : 'none',
          borderRadius: isTrainingMode ? 2 : 0,
          backgroundColor: isTrainingMode ? 'rgba(255, 152, 0, 0.04)' : 'transparent',
        }}
      >
        <Toolbar />
        {shouldShowDutyPrompt && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setOperatorDialogOpen(true);
                }}
              >
                Открыть текущее дежурство
              </Button>
            }
          >
            Укажите ФИО оперативного дежурного в текущем дежурстве
          </Alert>
        )}
        <Outlet />
      </Box>

      {/* Диалог выбора оператора */}
      {isDutyRole(effectiveRole) && (
        <OperatorSelectionDialog
          open={operatorDialogOpen}
          onClose={handleDutyOperatorDialogClose}
        />
      )}
    </Box>
  );
};

export default RoleBasedLayout;
